// src/components/practice/tasks/ClozeFreeText.jsx
import React from 'react';
import { marked } from 'marked';
import { FlagButton } from '../PartDisplay';
import { X } from 'lucide-react';

const ClozeFreeText = React.memo(({
  partData,
  questions,
  examples,
  partAnswers,
  handleSingleAnswer,
  flaggedQuestions,
  toggleFlagQuestion,
  isMobileAnswerSheetOpen,
  onCloseMobileAnswerSheet
}) => {
    const allProps = { flaggedQuestions, toggleFlagQuestion };
    const { content } = partData;
    const isNotesFormat = partData.partId === "listening-part-2";
    const mainText = content?.mainText || '';
    const placeholderRegex = /\((\d+)\)/g;

    // --- LEFT COLUMN CONTENT (Reading) ---
    const leftContent = (
      <div className="space-y-6">


        {/* Main Text with Gaps */}
        <div className="border-2 border-gray-900 bg-white p-6 rounded-none">
            <div className="prose prose-sm max-w-none text-gray-900 leading-relaxed whitespace-pre-wrap">
              {mainText.split(placeholderRegex).map((part, index) => {
                  if (index % 2 === 1) { // This part is a blank number
                      const blankNumber = part;
                      if (blankNumber === '0') {
  return (
    <span key={`blank-${index}`}
          className="inline-block text-center font-bold mx-1 px-3 py-1 bg-gray-100 border-2 border-gray-900 rounded-none">
      (0)
    </span>
  );
}
                      // This is a regular question gap
                      return (
                          <span key={`blank-${index}`} className="inline-block text-center font-bold mx-1 px-3 py-1 bg-gray-100 border-2 border-gray-900 rounded-none">
                              ({blankNumber})
                          </span>
                      );
                  }
                  // This is a regular text part
                  return <span key={`text-${index}`} dangerouslySetInnerHTML={{ __html: marked.parseInline(part, { breaks: true }) }} />;
              })}
            </div>
        </div>
      </div>
    );


    // --- RIGHT COLUMN CONTENT (Answering) ---
    const rightContent = (
      <div className="space-y-1">
        {/* Answer Sheet Header */}
        <div className="border-2 border-gray-900 bg-white p-3 rounded-none mb-4">
          <h3 className="text-lg font-bold text-gray-900 uppercase tracking-wide">
            Antwortbogen
          </h3>
          <p className="text-xs text-gray-600 mt-1">
            Tragen Sie Ihre Antworten ein.
          </p>
        </div>

        {/* --- Examples List --- */}
        {examples.map(ex => {
          const questionId = ex.questionId;
          const correctAnswer = Array.isArray(ex.correctAnswer) ? ex.correctAnswer[0] : ex.correctAnswer;

          return (
            <div
              key={questionId}
              className="border border-gray-900 bg-yellow-50 p-4 rounded-none opacity-90"
            >
              {/* Example Label */}
              <div className="flex items-start justify-between mb-2">
                <label
                  htmlFor={`input-${questionId}`}
                  className="flex-1 font-bold text-sm"
                >
                  {ex.blankNumber}.
                </label>
                {/* No FlagButton for examples */}
              </div>

              {/* Disabled Text Input Field */}
              <input
                id={`input-${questionId}`}
                type="text"
                value={correctAnswer || ''}
                disabled
                className="w-full px-3 py-2 border-2 border-gray-900 rounded-none text-sm bg-gray-100 cursor-not-allowed font-semibold"
              />
            </div>
          );
        })}

        {/* Questions List (Input Fields) */}
        {questions.map(q => {
          const questionId = q.questionId;
          const currentAnswer = partAnswers[questionId] || '';

          return (
            <div
              key={questionId}
              className="border border-gray-900 bg-white p-4 rounded-none hover:bg-gray-50 transition-colors"
            >
              {/* Question Label with Flag */}
              <div className="flex items-start justify-between mb-2">
                <label
                  htmlFor={`input-${questionId}`}
                  className="flex-1 font-bold text-sm"
                >
                  {q.blankNumber}.
                </label>
                <FlagButton {...allProps} questionId={questionId} />
              </div>

              {/* Text Input Field - scroll-margin so focus doesn't push header out of view */}
              <input
                id={`input-${questionId}`}
                type="text"
                value={currentAnswer}
                onChange={(e) => handleSingleAnswer(questionId, e.target.value)}
                className="w-full px-3 py-2 border-2 border-gray-900 rounded-none text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-1 [scroll-margin-top:4rem]"
                placeholder={`Antwort für (${q.blankNumber})`}
              />
            </div>
          );
        })}
      </div>
    );
    // --- NOTES FORMAT LAYOUT (for ÖSD C1 Listening Part 2) ---
if (isNotesFormat) {
  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-6">
      {/* Notes Header with Pencil Decorations */}
      <div className="bg-gray-100 border-2 border-gray-900 rounded-none p-6 mb-6 text-center">
        <div className="flex items-center justify-center gap-4 mb-2">
          <span className="text-2xl">✏️</span>
          <span className="text-2xl">✏️</span>
          <span className="text-2xl">✏️</span>
          <h2 className="text-3xl font-bold text-gray-900 uppercase tracking-wider">NOTIZEN</h2>
          <span className="text-2xl">✏️</span>
          <span className="text-2xl">✏️</span>
          <span className="text-2xl">✏️</span>
        </div>
      </div>

      {/* Notes Content */}
      <div className="bg-white border-2 border-gray-900 rounded-none p-8 shadow-lg">
        <div className="prose prose-sm max-w-none text-gray-900 leading-relaxed">
          {mainText.split(placeholderRegex).map((part, index) => {
            if (index % 2 === 1) {
              const blankNumber = part;
              const questionId = questions.find(q => q.blankNumber === parseInt(blankNumber))?.questionId;
              const currentAnswer = questionId ? (partAnswers[questionId] || '') : '';

              if (blankNumber === '0') {
                return (
                  <span key={`blank-${index}`} className="inline-block mx-1">
                    <strong>(0)</strong>
                  </span>
                );
              }

              return (
                <span key={`blank-${index}`} className="inline-block relative mx-1">
                  <span className="font-bold mr-1">({blankNumber})</span>
                  <input
                    type="text"
                    value={currentAnswer}
                    onChange={(e) => questionId && handleSingleAnswer(questionId, e.target.value)}
                    className="inline-block w-48 border-b-2 border-gray-900 bg-transparent focus:outline-none focus:border-blue-600 px-1"
                    style={{ borderTop: 'none', borderLeft: 'none', borderRight: 'none' }}
                  />
                </span>
              );
            }
            return <span key={`text-${index}`} dangerouslySetInnerHTML={{ __html: marked.parseInline(part, { breaks: true }) }} />;
          })}
        </div>
      </div>
    </div>
  );
}
    const mobileOverlay = (isMobileAnswerSheetOpen && onCloseMobileAnswerSheet) ? (
      <div className="fixed inset-0 z-[60] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog" aria-label="Antwortbogen">
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onCloseMobileAnswerSheet} aria-hidden />
        <div className="relative bg-white rounded-t-2xl shadow-2xl max-h-[90vh] flex flex-col animate-slide-up">
          <div className="flex items-center justify-between flex-shrink-0 p-3 border-b border-gray-200 bg-gray-50 rounded-t-2xl">
            <h3 className="text-lg font-bold text-gray-900">Antwortbogen</h3>
            <button type="button" onClick={onCloseMobileAnswerSheet} className="p-2 -m-2 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors touch-manipulation min-w-[48px] min-h-[48px] flex items-center justify-center" aria-label="Schließen">
              <X className="w-6 h-6 text-gray-700" />
            </button>
          </div>
          <div className="overflow-y-auto overscroll-contain p-4 pb-safe">{rightContent}</div>
        </div>
      </div>
    ) : null;

    return (
      <div className="w-full max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-6 lg:px-8 lg:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 lg:gap-8">

          {/* Left Column (Text) - sticky so it stays visible when user scrolls or focuses inputs */}
          <div className="lg:col-span-3 min-w-0 lg:sticky lg:top-6 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
            {leftContent}
          </div>

          {/* Right Column (Answer Sheet) - hidden on mobile; shown in overlay */}
          <div className="hidden lg:flex lg:col-span-2 mt-6 lg:mt-0 min-h-0 flex-col">
            <div className="min-h-0 max-h-[60vh] sm:max-h-[65vh] overflow-y-auto rounded border-2 border-gray-200 bg-white lg:max-h-[calc(100vh-6rem)] lg:sticky lg:top-6"
                 style={{ scrollBehavior: 'smooth' }}>
              {rightContent}
            </div>
          </div>

        </div>
        {mobileOverlay}
      </div>
    );
});

export default ClozeFreeText;